import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import ListAltIcon from "@mui/icons-material/ListAlt";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { fieldSx } from "../../egresos/components/formStyles";
import {
  createFacItem,
  getDosificacionGruposAdmin,
  getFacItemsAdmin,
  getHippoParametro,
  getServiciosFacturacion,
  updateDosificacionGrupoServicios,
  updateFacItem,
} from "../services/facturacionService";

const cellSx = { py: 0.6, px: 1, fontSize: "0.8rem", verticalAlign: "middle" };
const headCellSx = {
  ...cellSx,
  fontWeight: 800,
  fontSize: "0.7rem",
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: "text.secondary",
  whiteSpace: "nowrap",
  bgcolor: "background.paper",
  borderBottom: 2,
  borderColor: "divider",
};
const today = () => new Date().toISOString().slice(0, 10);
const unwrap = (res) => res?.data?.data ?? res?.data;
const getErrorMessage = (error, fallback = "Ocurrio un error") =>
  error?.response?.data?.message || error?.message || fallback;

// Estados de un item (columna `estado`): A activo, I inactivo, X eliminado
const ESTADOS_ITEM = {
  A: { texto: "Activo", color: "success" },
  I: { texto: "Inactivo", color: "warning" },
  X: { texto: "Eliminado", color: "default" },
};

const parseProductos = (items = []) => {
  const map = {};
  for (const row of Array.isArray(items) ? items : []) {
    const codigo = String(row.codigo || "").trim();
    if (!codigo) continue;
    const [codProduccion, codProducto, arancelario = ""] = codigo.split("-", 3).map((x) => String(x || "").trim());
    if (!codProduccion || !codProducto) continue;
    const valor = arancelario ? `${codProducto}-${arancelario}` : codProducto;
    if (!map[codProduccion]) map[codProduccion] = [];
    map[codProduccion].push({
      nombre: row.nombre || "",
      codigo_completo: codigo,
      cod_producto_servicio: codProducto,
      cod_arancelario: arancelario,
      valor_guardar: valor,
    });
  }
  return map;
};

const buildEmptyForm = (grupo) => ({
  id: null,
  item: "",
  estado: "A",
  servicio: "",
  unidad_de_medida: "58",
  cod_producto_servicio: "",
  fecha_registro: today(),
  cod_produccion: grupo?.cod || "",
  para_conteo: false,
});

const findProducto = (productos, codProduccion, valor) => {
  const lista = productos[codProduccion] || [];
  return lista.find((p) => p.valor_guardar === valor || p.cod_producto_servicio === valor);
};

// Texto auxiliar gris bajo un campo del formulario
const Ayuda = ({ children }) => (
  <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.3, mt: -0.25 }}>{children}</Typography>
);

const FacItemsTab = () => {
  const [grupos, setGrupos] = useState([]);
  const [servicios, setServicios] = useState([]);
  const [actividades, setActividades] = useState({});
  const [productos, setProductos] = useState({});
  const [unidades, setUnidades] = useState({});
  const [selectedGrupo, setSelectedGrupo] = useState(null);
  const [serviciosGrupo, setServiciosGrupo] = useState(null);
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(buildEmptyForm(null));
  const [itemDialogOpen, setItemDialogOpen] = useState(false);
  const [serviciosDialogOpen, setServiciosDialogOpen] = useState(false);
  const [serviciosSeleccionados, setServiciosSeleccionados] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [busquedaGrupo, setBusquedaGrupo] = useState("");
  const [busquedaItem, setBusquedaItem] = useState("");
  const [filtroEstado, setFiltroEstado] = useState("A"); // A = activos, otros = inactivos y eliminados, todos

  const productosGrupo = useMemo(
    () => productos[String(selectedGrupo?.cod || "")] || [],
    [productos, selectedGrupo]
  );

  const loadBase = async () => {
    setLoading(true);
    setError(null);
    try {
      const [resGrupos, resServicios, resActividades, resProductos, resUnidades] = await Promise.all([
        getDosificacionGruposAdmin(),
        getServiciosFacturacion(),
        getHippoParametro("Actividades"),
        getHippoParametro("ListaProductosServicios"),
        getHippoParametro("ParametricaUnidadMedida"),
      ]);
      const gruposData = unwrap(resGrupos);
      const actividadesData = unwrap(resActividades);
      const unidadesData = unwrap(resUnidades);

      setGrupos(Array.isArray(gruposData) ? gruposData : []);
      setServicios(Array.isArray(unwrap(resServicios)) ? unwrap(resServicios) : []);
      setActividades(Object.fromEntries((Array.isArray(actividadesData) ? actividadesData : []).map((a) => [String(a.codigo), a.nombre])));
      setProductos(parseProductos(unwrap(resProductos)));
      setUnidades(Object.fromEntries((Array.isArray(unidadesData) ? unidadesData : []).map((u) => [String(u.codigo), u.nombre])));
    } catch (e) {
      setError(getErrorMessage(e, "No se pudieron cargar los catalogos"));
    } finally {
      setLoading(false);
    }
  };

  const loadItems = async (grupo = selectedGrupo) => {
    if (!grupo?.cod) return;
    setLoadingItems(true);
    setError(null);
    try {
      const res = await getFacItemsAdmin({ cod_produccion: grupo.cod, estado: "Todos" });
      const data = unwrap(res);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(getErrorMessage(e, "No se pudieron cargar los items"));
    } finally {
      setLoadingItems(false);
    }
  };

  useEffect(() => {
    loadBase();
  }, []);

  const openGrupo = async (grupo) => {
    setMessage(null);
    setSelectedGrupo(grupo);
    setItems([]);
    setBusquedaItem("");
    setFiltroEstado("A");
    await loadItems(grupo);
  };

  const volverAGrupos = () => {
    setSelectedGrupo(null);
    setItems([]);
    loadBase(); // los conteos de items pueden haber cambiado
  };

  const openNewItem = () => {
    setMessage(null);
    setForm(buildEmptyForm(selectedGrupo));
    setItemDialogOpen(true);
  };

  const openEditItem = (item) => {
    setMessage(null);
    const prod = findProducto(productos, String(item.cod_produccion || selectedGrupo?.cod || ""), item.cod_producto_servicio);
    setForm({
      id: item.id,
      item: item.item || "",
      estado: item.estado || "A",
      servicio: item.servicio || "",
      unidad_de_medida: item.unidad_de_medida ?? "58",
      cod_producto_servicio: prod?.valor_guardar || item.cod_producto_servicio || "",
      fecha_registro: String(item.fecha_registro || "").slice(0, 10) || today(),
      cod_produccion: item.cod_produccion || selectedGrupo?.cod || "",
      para_conteo: Boolean(Number(item.para_conteo)),
    });
    setItemDialogOpen(true);
  };

  const openServiciosGrupo = (grupo) => {
    setMessage(null);
    setServiciosGrupo(grupo);
    setServiciosSeleccionados(String(grupo.servicios_ids || "").split(",").filter(Boolean).map(Number));
    setServiciosDialogOpen(true);
  };

  const toggleServicio = (id) => {
    setServiciosSeleccionados((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const saveServiciosGrupo = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await updateDosificacionGrupoServicios(serviciosGrupo.id, serviciosSeleccionados);
      setMessage("Servicios del grupo actualizados");
      setServiciosDialogOpen(false);
      setServiciosGrupo(null);
      await loadBase();
    } catch (e) {
      setError(getErrorMessage(e, "No se pudieron guardar los servicios"));
    } finally {
      setSaving(false);
    }
  };

  const saveItem = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const payload = {
        ...form,
        cod_produccion: Number(form.cod_produccion),
        unidad_de_medida: Number(form.unidad_de_medida),
      };
      if (form.id) await updateFacItem(form.id, payload);
      else await createFacItem(payload);
      setMessage(form.id ? "Item actualizado" : "Item creado");
      setItemDialogOpen(false);
      await loadItems();
    } catch (e) {
      setError(getErrorMessage(e, "No se pudo guardar el item"));
    } finally {
      setSaving(false);
    }
  };

  const productoActual = findProducto(productos, String(selectedGrupo?.cod || form.cod_produccion || ""), form.cod_producto_servicio);

  // ---- listas filtradas ----
  const gruposVisibles = useMemo(() => {
    const q = busquedaGrupo.trim().toLowerCase();
    if (!q) return grupos;
    return grupos.filter((g) => [g.nombre, g.cod, g.observacion, g.servicios, actividades[String(g.cod)]].join(" ").toLowerCase().includes(q));
  }, [grupos, busquedaGrupo, actividades]);

  const conteoEstados = useMemo(() => ({
    A: items.filter((i) => i.estado === "A").length,
    otros: items.filter((i) => i.estado !== "A").length,
    todos: items.length,
  }), [items]);

  const itemsVisibles = useMemo(() => {
    const q = busquedaItem.trim().toLowerCase();
    return items.filter((i) => {
      if (filtroEstado === "A" && i.estado !== "A") return false;
      if (filtroEstado === "otros" && i.estado === "A") return false;
      if (!q) return true;
      const prod = findProducto(productos, String(i.cod_produccion), i.cod_producto_servicio);
      return [i.item, prod?.nombre, i.cod_producto_servicio].join(" ").toLowerCase().includes(q);
    });
  }, [items, filtroEstado, busquedaItem, productos]);

  return (
    <Box sx={{ p: 1 }}>
      <Snackbar
        open={Boolean(message)}
        autoHideDuration={3500}
        onClose={() => setMessage(null)}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" variant="filled" onClose={() => setMessage(null)} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
      {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}

      {!selectedGrupo ? (
        <>
          {/* ── Qué es esta pantalla ── */}
          <Alert severity="info" icon={<InfoOutlinedIcon fontSize="small" />} sx={{ mb: 1, py: 0.25, fontSize: "0.8rem", alignItems: "center" }}>
            <b>¿Cómo funciona?</b> Cada grupo es una <b>actividad económica</b> (código de producción de Hippo/SIN). Dentro de cada grupo están los
            <b> items</b> que se pueden facturar. Un servicio factura con los items del grupo al que está vinculado: usa
            <b> “Servicios”</b> para vincularlo y <b>“Ver items”</b> para ver o cambiar lo que se factura.
          </Alert>

          <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2 }}>Grupos de facturación</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                {loading ? "Cargando…" : `${gruposVisibles.length} grupo${gruposVisibles.length === 1 ? "" : "s"}`}
              </Typography>
            </Box>
            <TextField
              size="small" placeholder="Buscar grupo, código o servicio…" value={busquedaGrupo} onChange={(e) => setBusquedaGrupo(e.target.value)}
              sx={{ ...fieldSx, width: { xs: "100%", sm: 300 } }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            />
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={loadBase} disabled={loading} sx={{ textTransform: "none" }}>
              Actualizar
            </Button>
          </Paper>

          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Grupo</TableCell>
                    <TableCell sx={headCellSx}>Código de producción</TableCell>
                    <TableCell sx={headCellSx}>Servicios que lo usan</TableCell>
                    <TableCell sx={headCellSx} align="center">Items activos</TableCell>
                    <TableCell sx={headCellSx} align="center">Estado</TableCell>
                    <TableCell sx={headCellSx} align="right">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ ...cellSx, py: 3 }}><CircularProgress size={22} /></TableCell></TableRow>
                  ) : gruposVisibles.length === 0 ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ ...cellSx, py: 3, color: "text.secondary" }}>{busquedaGrupo ? "Ningún grupo coincide con la búsqueda." : "No hay grupos para mostrar."}</TableCell></TableRow>
                  ) : gruposVisibles.map((grupo) => {
                    const listaServicios = String(grupo.servicios || "").split(",").map((x) => x.trim()).filter(Boolean);
                    const activo = grupo.estado === "Activo";
                    return (
                      <TableRow key={grupo.id} hover sx={{ "&:nth-of-type(even)": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
                        <TableCell sx={{ ...cellSx, maxWidth: 340 }}>
                          <Typography sx={{ fontSize: "0.85rem", fontWeight: 800, lineHeight: 1.25 }}>{grupo.nombre}</Typography>
                          {grupo.observacion && (
                            <Tooltip title={grupo.observacion} enterDelay={500}>
                              <Typography sx={{ fontSize: "0.72rem", color: "text.secondary", lineHeight: 1.25, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                                {grupo.observacion}
                              </Typography>
                            </Tooltip>
                          )}
                        </TableCell>
                        <TableCell sx={{ ...cellSx, maxWidth: 260 }}>
                          <Typography sx={{ fontFamily: "monospace", fontWeight: 700, fontSize: "0.82rem" }}>{grupo.cod}</Typography>
                          <Typography sx={{ fontSize: "0.72rem", color: actividades[String(grupo.cod)] ? "text.secondary" : "warning.main", lineHeight: 1.25 }}>
                            {actividades[String(grupo.cod)] || "Sin descripción en Hippo"}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>
                          {listaServicios.length ? (
                            <Box sx={{ display: "flex", gap: 0.4, flexWrap: "wrap" }}>
                              {listaServicios.map((s) => <Chip key={s} size="small" label={s} sx={{ height: 20, fontSize: 11 }} />)}
                            </Box>
                          ) : (
                            <Chip size="small" variant="outlined" color="warning" label="Ningún servicio vinculado" sx={{ height: 20, fontSize: 11 }} />
                          )}
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Chip
                            size="small" color={Number(grupo.items_activos) ? "primary" : "default"} variant={Number(grupo.items_activos) ? "filled" : "outlined"}
                            label={Number(grupo.items_activos ?? 0)} sx={{ height: 20, minWidth: 30, fontWeight: 800, fontSize: 11.5 }}
                          />
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Chip size="small" color={activo ? "success" : "default"} variant="outlined" label={grupo.estado || "-"} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }} align="right">
                          <Button size="small" variant="contained" startIcon={<ListAltIcon />} onClick={() => openGrupo(grupo)} sx={{ textTransform: "none", fontSize: "0.74rem", mr: 0.5 }}>
                            Ver items
                          </Button>
                          <Button size="small" variant="outlined" startIcon={<LinkIcon />} onClick={() => openServiciosGrupo(grupo)} sx={{ textTransform: "none", fontSize: "0.74rem" }}>
                            Servicios
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      ) : (
        <>
          {/* ── Encabezado del grupo ── */}
          <Paper variant="outlined" sx={{ p: 1, mb: 1, display: "flex", alignItems: "center", gap: 1.25, flexWrap: "wrap" }}>
            <Button size="small" variant="outlined" color="inherit" startIcon={<ArrowBackIcon />} onClick={volverAGrupos} sx={{ textTransform: "none" }}>
              Volver a grupos
            </Button>
            <Box sx={{ flex: 1, minWidth: 220 }}>
              <Typography sx={{ fontSize: 15, fontWeight: 800, lineHeight: 1.2 }}>{selectedGrupo.nombre}</Typography>
              <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                Producción <b style={{ fontFamily: "monospace" }}>{selectedGrupo.cod}</b> · {actividades[String(selectedGrupo.cod)] || "Sin descripción en Hippo"}
              </Typography>
            </Box>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={() => loadItems()} disabled={loadingItems} sx={{ textTransform: "none" }}>Actualizar</Button>
            <Button size="small" variant="contained" startIcon={<AddIcon />} onClick={openNewItem} sx={{ textTransform: "none", fontWeight: 700 }}>Adicionar item</Button>
          </Paper>

          {/* ── Filtros ── */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap", mb: 1 }}>
            <ToggleButtonGroup
              size="small" exclusive value={filtroEstado} onChange={(_, v) => v && setFiltroEstado(v)}
              sx={{ bgcolor: "background.paper", "& .MuiToggleButton-root": { py: 0.4, px: 1.5, fontSize: 12.5, textTransform: "none", fontWeight: 700 } }}
            >
              <ToggleButton value="A">Activos ({conteoEstados.A})</ToggleButton>
              <ToggleButton value="otros" disabled={!conteoEstados.otros}>Inactivos y eliminados ({conteoEstados.otros})</ToggleButton>
              <ToggleButton value="todos">Todos ({conteoEstados.todos})</ToggleButton>
            </ToggleButtonGroup>
            <Box sx={{ flex: 1 }} />
            <TextField
              size="small" placeholder="Buscar item o producto…" value={busquedaItem} onChange={(e) => setBusquedaItem(e.target.value)}
              sx={{ ...fieldSx, width: { xs: "100%", sm: 280 } }}
              slotProps={{ input: { startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> } }}
            />
          </Box>

          <Paper variant="outlined" sx={{ overflow: "hidden" }}>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={headCellSx}>Item (como sale en la factura)</TableCell>
                    <TableCell sx={headCellSx}>Producto o servicio del SIN</TableCell>
                    <TableCell sx={headCellSx}>Unidad de medida</TableCell>
                    <TableCell sx={headCellSx} align="center">
                      <Tooltip title="Si cuenta, la cantidad de este item se suma en la columna “Cant.” del reporte de facturas del cliente">
                        <span>Suma en “Cant.”</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell sx={headCellSx} align="center">Estado</TableCell>
                    <TableCell sx={headCellSx} align="right">Acción</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loadingItems ? (
                    <TableRow><TableCell colSpan={6} align="center" sx={{ ...cellSx, py: 3 }}><CircularProgress size={22} /></TableCell></TableRow>
                  ) : itemsVisibles.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" sx={{ ...cellSx, py: 3, color: "text.secondary" }}>
                        {items.length === 0 ? "Este grupo todavía no tiene items. Usa “Adicionar item” para crear el primero." : "Ningún item coincide con el filtro."}
                      </TableCell>
                    </TableRow>
                  ) : itemsVisibles.map((item) => {
                    const prod = findProducto(productos, String(item.cod_produccion), item.cod_producto_servicio);
                    const est = ESTADOS_ITEM[item.estado] || { texto: item.estado || "-", color: "default" };
                    return (
                      <TableRow key={item.id} hover sx={{ opacity: item.estado === "A" ? 1 : 0.65, "&:nth-of-type(even)": { bgcolor: (t) => alpha(t.palette.text.primary, 0.02) } }}>
                        <TableCell sx={{ ...cellSx, fontWeight: 800, fontSize: "0.85rem" }}>{item.item}</TableCell>
                        <TableCell sx={cellSx}>
                          <Typography sx={{ fontSize: "0.8rem", lineHeight: 1.25 }}>{prod?.nombre || item.cod_producto_servicio || "Sin descripción"}</Typography>
                          <Typography sx={{ fontSize: "0.7rem", color: "text.secondary", fontFamily: "monospace" }}>
                            {item.cod_producto_servicio || "-"}{prod?.cod_arancelario ? ` · arancel ${prod.cod_arancelario}` : ""}
                          </Typography>
                        </TableCell>
                        <TableCell sx={cellSx}>{unidades[String(item.unidad_de_medida)] || item.unidad_de_medida || "Sin unidad"}</TableCell>
                        <TableCell sx={cellSx} align="center">
                          {Number(item.para_conteo)
                            ? <Chip size="small" color="primary" label="Sí cuenta" sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                            : <Typography component="span" sx={{ color: "text.disabled", fontSize: "0.78rem" }}>—</Typography>}
                        </TableCell>
                        <TableCell sx={cellSx} align="center">
                          <Chip size="small" color={est.color} variant={item.estado === "A" ? "filled" : "outlined"} label={est.texto} sx={{ height: 20, fontSize: 11, fontWeight: 700 }} />
                        </TableCell>
                        <TableCell sx={cellSx} align="right">
                          <Button size="small" variant="outlined" startIcon={<EditIcon />} onClick={() => openEditItem(item)} sx={{ textTransform: "none", fontSize: "0.74rem" }}>
                            Editar
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </>
      )}

      {/* ── Crear / editar un item ── */}
      <Dialog open={itemDialogOpen} onClose={() => !saving && setItemDialogOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          {form.id ? "Editar item de factura" : "Adicionar item"}
          <Typography component="span" sx={{ ml: 1, fontSize: 12, fontWeight: 400, color: "text.secondary" }}>
            en {selectedGrupo?.nombre}
          </Typography>
        </DialogTitle>
        <Box component="form" onSubmit={saveItem}>
          <DialogContent dividers sx={{ p: 1.5 }}>
            <Box sx={{ display: "grid", gap: 1.25 }}>
              <Box sx={{ display: "grid", gap: 0.4 }}>
                <TextField size="small" label="Nombre del item" value={form.item} onChange={(e) => setForm((p) => ({ ...p, item: e.target.value }))} sx={fieldSx} required />
                <Ayuda>Es el nombre que aparece al elegir el item en una línea de factura. Ej.: AVL_NACIONAL.</Ayuda>
              </Box>
              <Box sx={{ display: "grid", gap: 0.4 }}>
                <TextField select size="small" label="Producto o servicio (catálogo del SIN)" value={form.cod_producto_servicio} onChange={(e) => setForm((p) => ({ ...p, cod_producto_servicio: e.target.value }))} sx={fieldSx} required>
                  {productosGrupo.length ? productosGrupo.map((p) => (
                    <MenuItem key={p.valor_guardar} value={p.valor_guardar}>
                      {p.nombre}{p.cod_arancelario ? ` | ${p.cod_arancelario}` : ""}
                    </MenuItem>
                  )) : <MenuItem value="">No existen productos para esta producción</MenuItem>}
                </TextField>
                {productoActual
                  ? <Ayuda>Código completo: <b style={{ fontFamily: "monospace" }}>{productoActual.codigo_completo}</b></Ayuda>
                  : <Ayuda>Solo aparecen los productos que Hippo/SIN habilita para este código de producción.</Ayuda>}
              </Box>
              <TextField select size="small" label="Unidad de medida" value={form.unidad_de_medida} onChange={(e) => setForm((p) => ({ ...p, unidad_de_medida: e.target.value }))} sx={fieldSx} required>
                {Object.entries(unidades).map(([codigo, nombre]) => (
                  <MenuItem key={codigo} value={codigo}>{nombre}</MenuItem>
                ))}
              </TextField>
              <Box sx={{ display: "grid", gap: 0.4 }}>
                <TextField select size="small" label="Estado" value={form.estado} onChange={(e) => setForm((p) => ({ ...p, estado: e.target.value }))} sx={fieldSx}>
                  <MenuItem value="A">Activo</MenuItem>
                  <MenuItem value="I">Inactivo</MenuItem>
                  <MenuItem value="X">Eliminado</MenuItem>
                </TextField>
                <Ayuda>Solo los items activos se pueden elegir al facturar. Para dejar de usar un item, márcalo como inactivo o eliminado.</Ayuda>
              </Box>
              <Box sx={{ border: 1, borderColor: "divider", px: 1, py: 0.25 }}>
                <FormControlLabel
                  control={(
                    <Checkbox
                      size="small"
                      checked={form.para_conteo}
                      onChange={(e) => setForm((p) => ({ ...p, para_conteo: e.target.checked }))}
                    />
                  )}
                  label="Sumar en la columna “Cant.” del reporte"
                  sx={{ m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.82rem", fontWeight: 600 } }}
                />
                <Ayuda>Si está marcado, la cantidad de este item se suma en la columna “Cant.” del reporte de facturas del cliente.</Ayuda>
              </Box>
            </Box>
          </DialogContent>
          <DialogActions sx={{ p: 1 }}>
            <Button size="small" variant="outlined" color="inherit" onClick={() => setItemDialogOpen(false)} disabled={saving}>Cerrar</Button>
            <Button type="submit" size="small" variant="contained" startIcon={saving ? <CircularProgress size={14} color="inherit" /> : <SaveIcon />} disabled={saving}>Guardar</Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* ── Servicios vinculados a un grupo ── */}
      <Dialog
        open={serviciosDialogOpen}
        onClose={() => {
          if (saving) return;
          setServiciosDialogOpen(false);
          setServiciosGrupo(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ py: 1, fontSize: "0.95rem", fontWeight: 900 }}>
          Servicios del grupo
          <Typography sx={{ fontSize: 12, fontWeight: 400, color: "text.secondary" }}>
            Marca los servicios que facturan con los items de <b>{serviciosGrupo?.nombre}</b>
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ p: 1 }}>
          {servicios.map((servicio) => (
            <FormControlLabel
              key={servicio.id_servicio}
              control={<Checkbox size="small" checked={serviciosSeleccionados.includes(Number(servicio.id_servicio))} onChange={() => toggleServicio(Number(servicio.id_servicio))} />}
              label={servicio.nombre}
              sx={{ display: "block", m: 0, "& .MuiFormControlLabel-label": { fontSize: "0.82rem" } }}
            />
          ))}
        </DialogContent>
        <DialogActions sx={{ p: 1 }}>
          <Button
            size="small"
            variant="outlined"
            color="inherit"
            onClick={() => {
              setServiciosDialogOpen(false);
              setServiciosGrupo(null);
            }}
            disabled={saving}
          >
            Volver
          </Button>
          <Button size="small" variant="contained" onClick={saveServiciosGrupo} disabled={saving}>Guardar servicios</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FacItemsTab;
